import styled from "@emotion/styled";
import { usePrevious } from "@react-hookz/web";
import { genId } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { UseMultipleSelectionActions, UseMultipleSelectionPropGetters } from "downshift";
import React, { HTMLAttributes, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useI18n } from "../../../../i18n/i18n";
import { RequiredThemeKeys } from "../../../../util/theme";
import { useCombinedThemeMode } from "../../../../util/theme/useCombinedThemeMode";
import { AvatarProps } from "../../../avatar";
import { AvatarChip, Chip } from "../../../chip";
import { Inline } from "../../../layout";
import { BaseOptionType, FormatMultiSelectValue, RenderValueProps } from "../../types";
import { stopPropagationHandler } from "../../utils";

interface MultiSelectValueContainerStyledProps {
    maxHeight: number;
    themeMode: RequiredThemeKeys;
}

const MultiSelectValueContainerStyled = styled(Inline)<MultiSelectValueContainerStyledProps>`
    padding: 6px 0 6px 6px;
    flex-grow: 1;
    box-sizing: border-box;
    max-height: ${({ maxHeight }) => maxHeight}px;

    // For Chip eliding.
    min-width: 1px;
    overflow: auto;
    display: flex;

    // Targeting the Inline's inner wrapper div.
    > div:first-of-type {
        height: 100%;
        margin: 0px;
        gap: 4px;

        // targeting the chips (or whatever children) to get rid of the padding they come in with
        > * {
            margin: 0;
        }
    }
`;

interface MultiSelectValueContainerProps<Option extends BaseOptionType>
    extends Pick<UseMultipleSelectionPropGetters<Option>, "getSelectedItemProps">,
        Pick<UseMultipleSelectionActions<Option>, "removeSelectedItem" | "setSelectedItems" | "setActiveIndex"> {
    activeIndex: number;
    isReadonly: boolean;
    maxSelectedRows: number;
    numSelected: number;
    onChipNavigation: (direction: "tab" | "down" | "up") => void;
    onHeightChange: (newHeight: number) => void;
    optionToString: (option: Option) => string;
    rollupSelectionAfter: number | undefined;
    selectedItems: Option[];
    children?: ReactNode;
    clientId?: string;
    formatSelectedValue?: FormatMultiSelectValue<Option>;
    id?: string;
}

export const MultiSelectValueContainer = <Option extends BaseOptionType>({
    activeIndex,
    children,
    clientId,
    getSelectedItemProps,
    id,
    isReadonly = false,
    maxSelectedRows,
    rollupSelectionAfter,
    numSelected,
    onHeightChange,
    optionToString,
    removeSelectedItem,
    selectedItems,
    setActiveIndex,
    setSelectedItems,
    onChipNavigation,
    formatSelectedValue,
}: MultiSelectValueContainerProps<Option>) => {
    const { i18n } = useI18n();
    const themeMode = useCombinedThemeMode();
    const [chipIdPrefix] = useState(() => genId("lodestar-ms-chip"));
    const containerRef = useRef<HTMLDivElement>(null);
    const innerWrapperRef = useRef<HTMLDivElement>(null);

    const isInteractive = !isReadonly;

    const containerElement = containerRef.current;
    useEffect(() => {
        if (window.ResizeObserver && containerElement) {
            const observer = new ResizeObserver((entry) => {
                const newVerticalSize = entry[0]?.contentBoxSize[0]?.blockSize || 0;
                onHeightChange(newVerticalSize);
            });
            observer.observe(containerElement);
            return () => {
                observer.disconnect();
            };
        }
    }, [containerElement, onHeightChange]);

    /*
    if (maxRows === 1) show 1 row
    if (maxRows === 2) show 1.5 rows
    if (maxRows === 3) show 2.5 rows
    ... on and on ...
    */
    const chipHeight = 28;
    const maxHeight =
        chipHeight + 8 + (maxSelectedRows > 1 ? chipHeight / 2 + (maxSelectedRows - 2) * (chipHeight + 4) : 0);

    // Only apply these props when we actually have children, otherwise the axe scanners
    // tell us that:
    // 1: a list box MUST have children.
    // 2: a div without a role shouldn't have a label.
    const includeAriaProps = numSelected > 0;
    const innerWrapperProps = useMemo(
        () => ({
            ref: innerWrapperRef,
            ...(includeAriaProps
                ? {
                      "aria-label": i18n._(
                          /*i18n*/ {
                              comment: "This is a label referring to all the items the user has selected",
                              id: "Selected Items",
                          }
                      ),
                      role: "listbox",
                  }
                : undefined),
        }),
        [includeAriaProps, i18n]
    );

    const selectedItemsToShow =
        rollupSelectionAfter === undefined ? selectedItems : selectedItems.slice(0, rollupSelectionAfter);
    const selectedValue = selectedItems.length - selectedItemsToShow.length;

    const handleChipNavigation: React.KeyboardEventHandler = (e) => {
        e.preventDefault();
        switch (e.key) {
            case "Tab":
                setActiveIndex(-1);
                onChipNavigation("tab");
                break;
            case "ArrowDown":
                if (isInteractive) {
                    onChipNavigation("down");
                }
                break;
            case "ArrowUp":
                if (isInteractive) {
                    onChipNavigation("up");
                }
                break;
        }
    };

    const selectionLength = selectedItems.length;
    const previousSelectionLength = usePrevious(selectionLength);
    const [hasFocus, setHasFocus] = useState(false);

    // If a chip previously had focus and the previously focused chip was deleted, we'll need
    // to move focus to a neighboring chip.
    useEffect(() => {
        if (hasFocus && previousSelectionLength !== selectionLength && activeIndex >= 0) {
            (Array.from(innerWrapperRef.current?.childNodes || []) as HTMLElement[])[activeIndex]?.focus();
        }
    }, [hasFocus, activeIndex, previousSelectionLength, selectionLength]);

    return (
        <MultiSelectValueContainerStyled
            space="xxSmall"
            maxHeight={maxHeight}
            ref={containerRef}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            innerWrapperProps={innerWrapperProps}
            themeMode={themeMode}
        >
            {selectedItemsToShow.map((option: Option, index: number) => {
                const asString = optionToString(option);
                const chipId = id ? `${id}--selected-item-${index}` : `${chipIdPrefix}_${index}`;
                const chipClientId = getClientId(clientId, "-selected-item");
                const chipProps: RenderValueProps = {
                    onRemove: isInteractive
                        ? (e) => {
                              e.stopPropagation();
                              removeSelectedItem(option);
                          }
                        : undefined,
                    // Do we need this still?
                    isFocused: activeIndex === index,
                    isReadonly,
                    children: null,
                    key: asString,
                    clientId: chipClientId,
                    ...(getSelectedItemProps({
                        index,
                        role: "option",
                        selectedItem: option,
                        id: chipId,
                        onClick: stopPropagationHandler,
                        onKeyDown: handleChipNavigation,
                    }) as HTMLAttributes<HTMLDivElement>),
                };

                if (formatSelectedValue) return formatSelectedValue(option, chipProps, false, 0);

                return option.avatarProps ? (
                    // We're good. key is part of chipProps.
                    // eslint-disable-next-line react/jsx-key
                    <AvatarChip {...chipProps} avatarProps={option.avatarProps as AvatarProps}>
                        {asString}
                    </AvatarChip>
                ) : (
                    <Chip {...chipProps} color="neutral">
                        {asString}
                    </Chip>
                );
            })}
            {selectedItemsToShow.length < selectedItems.length &&
                (formatSelectedValue !== undefined ? (
                    formatSelectedValue(
                        null,
                        {
                            onRemove: isInteractive
                                ? (e) => {
                                      e.stopPropagation();
                                      setActiveIndex(selectedItemsToShow.length - 1);
                                      setSelectedItems(selectedItemsToShow);
                                  }
                                : undefined,
                            children: null,
                            ...(getSelectedItemProps({
                                index: selectedItemsToShow.length,
                                role: "option",
                                selectedItem: undefined as unknown as Option,
                                id: `${chipIdPrefix}_${selectedItemsToShow.length}`,
                                onClick: stopPropagationHandler,
                            }) as HTMLAttributes<HTMLDivElement>),
                        },
                        true,
                        selectedValue
                    )
                ) : (
                    <Chip
                        onRemove={
                            isInteractive
                                ? (e) => {
                                      e.stopPropagation();
                                      setActiveIndex(selectedItemsToShow.length - 1);

                                      setSelectedItems(selectedItemsToShow);
                                  }
                                : undefined
                        }
                        {...getSelectedItemProps({
                            index: selectedItemsToShow.length,
                            role: "option",
                            selectedItem: undefined as unknown as Option,
                            id: `${chipIdPrefix}_${selectedItemsToShow.length}`,
                            onClick: stopPropagationHandler,
                        })}
                    >
                        {i18n._(
                            /*i18n*/ {
                                comment: "This is showing the number of selected items",
                                id: `{selectedValue} selected`,
                                values: { selectedValue },
                            }
                        )}
                    </Chip>
                ))}
            {children}
        </MultiSelectValueContainerStyled>
    );
};
